import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../store';
import {
	setSelectedRegions,
	setRegionLoading,
	setRegionMarkets,
	setRegionError,
	clearRegionData,
	MarketItem,
} from '../store/slices/analyticsHubSlice';
import { createApiClient } from '../api/apiClient';

// ─── Configuration ────────────────────────────────────────────────────────────

/** Debounce delay in ms before triggering API calls after region selection changes */
const DEBOUNCE_MS = 300;

/** Number of retry attempts for failed API calls */
const MAX_RETRIES = 3;

/** Delay between retries (doubles each attempt) */
const RETRY_BASE_DELAY_MS = 500;

/** Cache TTL in ms (5 minutes) — cached data older than this will be re-fetched */
const CACHE_TTL_MS = 5 * 60 * 1000;

// ─── API ──────────────────────────────────────────────────────────────────────

const API_BASE = '/admin/locations';

/**
 * Fetch markets for a single region with retry and abort support.
 */
async function fetchMarketsForRegion(
	region: string,
	signal: AbortSignal,
	retries = MAX_RETRIES
): Promise<MarketItem[]> {
	const api = createApiClient();
	let lastError: Error | null = null;

	for (let attempt = 0; attempt <= retries; attempt++) {
		if (signal.aborted) {
			throw new DOMException('Aborted', 'AbortError');
		}

		try {
			const params = new URLSearchParams({ region });
			const { data } = await api.get(`${API_BASE}/markets-by-region?${params}`, { signal });

			const raw: string[] = Array.isArray(data) ? data : (data.data ?? []);

			// Map raw strings to MarketItem objects
			return raw.map((name: string) => ({
				id: name,
				displayText: name,
			}));
		} catch (err: any) {
			// Don't retry if request was intentionally aborted
			if (err.name === 'AbortError' || err.code === 'ERR_CANCELED' || signal.aborted) {
				throw new DOMException('Aborted', 'AbortError');
			}

			lastError = err;

			// Wait before retry (exponential backoff)
			if (attempt < retries) {
				const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt);
				await new Promise(resolve => setTimeout(resolve, delay));
			}
		}
	}

	throw lastError ?? new Error(`Failed to fetch markets for region "${region}"`);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Custom hook that manages multi-region market fetching with:
 * - Debouncing (300ms)
 * - Per-region caching with TTL
 * - Retry logic (3 retries with exponential backoff)
 * - AbortController-based cancellation for deselected regions
 * - Redux state management via analyticsHubSlice
 *
 * @param selectedRegionIds - Array of currently selected region IDs/names
 */
export function useRegionMarkets(selectedRegionIds: string[]) {
	const dispatch = useAppDispatch();
	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
	const prevRegionsRef = useRef<string[]>([]);

	// Selectors
	const regionMarkets = useSelector((state: RootState) => state.analyticsHub.regionMarkets);
	const combinedMarkets = useSelector((state: RootState) => state.analyticsHub.combinedMarkets);
	const storeSelectedRegions = useSelector((state: RootState) => state.analyticsHub.selectedRegions);

	// Keep a ref of regionMarkets to avoid re-creating callbacks when Redux state changes
	const regionMarketsRef = useRef(regionMarkets);
	regionMarketsRef.current = regionMarkets;

	/**
	 * Determine if a region's cached data is still valid.
	 * Uses ref to avoid dependency on regionMarkets changing.
	 */
	const isCacheValid = useCallback(
		(region: string): boolean => {
			const entry = regionMarketsRef.current[region];
			if (!entry || !entry.fetchedAt) return false;
			return Date.now() - entry.fetchedAt < CACHE_TTL_MS;
		},
		[] // stable — reads from ref
	);

	/**
	 * Cancel an in-flight request for a specific region.
	 */
	const cancelRegionFetch = useCallback((region: string) => {
		const controller = abortControllersRef.current.get(region);
		if (controller) {
			controller.abort();
			abortControllersRef.current.delete(region);
		}
	}, []);

	/**
	 * Fetch markets for a single region (with caching check).
	 */
	const fetchRegion = useCallback(
		async (region: string) => {
			// Skip if already cached and valid
			if (isCacheValid(region)) {
				return;
			}

			// Cancel any existing in-flight request for this region
			cancelRegionFetch(region);

			// Create new AbortController
			const controller = new AbortController();
			abortControllersRef.current.set(region, controller);

			dispatch(setRegionLoading({ region }));

			try {
				const markets = await fetchMarketsForRegion(region, controller.signal);
				// Only dispatch if not aborted
				if (!controller.signal.aborted) {
					dispatch(setRegionMarkets({ region, markets }));
				}
			} catch (err: any) {
				if (err.name === 'AbortError') {
					// Request was cancelled — don't set error state
					return;
				}
				dispatch(setRegionError({ region, error: err.message || 'Failed to fetch markets' }));
			} finally {
				abortControllersRef.current.delete(region);
			}
		},
		[dispatch, isCacheValid, cancelRegionFetch]
	);

	/**
	 * Core logic: compare previous vs. new selection, cancel removed, fetch added.
	 */
	const reconcileRegions = useCallback(
		(newRegions: string[]) => {
			const prevRegions = prevRegionsRef.current;

			// Regions that were removed
			const removed = prevRegions.filter(r => !newRegions.includes(r));
			// Regions that were added
			const added = newRegions.filter(r => !prevRegions.includes(r));

			// Cancel in-flight requests for removed regions
			for (const region of removed) {
				cancelRegionFetch(region);
				dispatch(clearRegionData({ region }));
			}

			// Update Redux selected regions
			dispatch(setSelectedRegions(newRegions));

			// Fetch data for newly added regions (skip if cached)
			for (const region of added) {
				fetchRegion(region);
			}

			prevRegionsRef.current = newRegions;
		},
		[dispatch, cancelRegionFetch, fetchRegion]
	);

	/**
	 * Effect: debounce region selection changes.
	 */
	useEffect(() => {
		// Clear previous debounce timer
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		debounceTimerRef.current = setTimeout(() => {
			reconcileRegions(selectedRegionIds);
		}, DEBOUNCE_MS);

		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, [selectedRegionIds, reconcileRegions]);

	/**
	 * Cleanup: abort all in-flight requests on unmount.
	 */
	useEffect(() => {
		return () => {
			abortControllersRef.current.forEach(controller => controller.abort());
			abortControllersRef.current.clear();
		};
	}, []);

	// ─── Derived state ────────────────────────────────────────────────────────

	/** True if any selected region is currently loading */
	const isLoading = storeSelectedRegions.some((r: string) => regionMarkets[r]?.loading);

	/** Aggregate errors from regions that failed */
	const errors: Record<string, string> = {};
	for (const r of storeSelectedRegions) {
		const entry = regionMarkets[r];
		if (entry?.error) {
			errors[r] = entry.error;
		}
	}
	const hasErrors = Object.keys(errors).length > 0;

	/** Retry a specific failed region */
	const retryRegion = useCallback(
		(region: string) => {
			// Invalidate cache to force re-fetch
			dispatch(clearRegionData({ region }));
			fetchRegion(region);
		},
		[dispatch, fetchRegion]
	);

	return {
		/** Combined market list from all selected regions (de-duplicated, sorted) */
		combinedMarkets,
		/** Per-region market data map */
		regionMarkets,
		/** Whether any region is currently loading */
		isLoading,
		/** Map of region → error message for failed fetches */
		errors,
		/** Whether there are any errors */
		hasErrors,
		/** Retry a specific failed region fetch */
		retryRegion,
	};
}
